import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/auth";
import { useProjectStore } from "@/stores/projects";

const NAV_ITEMS = [
  { path: "/", label: "Chat", icon: "💬" },
  { path: "/projects", label: "Projects", icon: "📁" },
  { path: "/cron", label: "Cron", icon: "⏰" },
  { path: "/inbox", label: "Inbox", icon: "📬" },
];

export function DashboardLayout() {
  const { user, logout } = useAuthStore();
  const { projects, activeProject, setActiveProject } = useProjectStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? "w-60" : "w-14"
        } flex flex-col border-r border-zinc-800 bg-zinc-950 transition-all duration-200`}
      >
        {/* Logo */}
        <div className="flex h-14 items-center gap-2 border-b border-zinc-800 px-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-zinc-800"
          >
            🔥
          </button>
          {sidebarOpen && (
            <span className="text-sm font-semibold text-zinc-200">ACP</span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-1 p-2">
          {NAV_ITEMS.map((item) => {
            const active =
              item.path === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  active
                    ? "bg-zinc-800 text-white"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-200"
                }`}
              >
                <span className="text-base">{item.icon}</span>
                {sidebarOpen && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Projects list */}
        {sidebarOpen && (
          <div className="border-t border-zinc-800 p-2">
            <div className="mb-2 px-3 text-xs font-medium uppercase text-zinc-500">
              Projects
            </div>
            <div className="max-h-40 space-y-0.5 overflow-y-auto">
              {projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setActiveProject(p);
                    navigate(`/projects/${p.id}`);
                  }}
                  className={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-xs transition-colors ${
                    activeProject?.id === p.id
                      ? "bg-blue-600/10 text-blue-400"
                      : "text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300"
                  }`}
                >
                  <span className="truncate">{p.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* User */}
        <div className="border-t border-zinc-800 p-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
              {user?.username?.[0]?.toUpperCase() || "?"}
            </div>
            {sidebarOpen && (
              <div className="flex-1 truncate text-sm text-zinc-300">
                {user?.username}
              </div>
            )}
            {sidebarOpen && (
              <button
                onClick={() => {
                  logout();
                  navigate("/login");
                }}
                className="text-zinc-500 hover:text-zinc-300"
                title="Logout"
              >
                ⏻
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
